import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { SampleViewModel } from "@/services/backend/samples/types/sample-view-model";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { ChevronDown } from "lucide-react";
import {
  useSamplesDeliveryListQuery,
  useSamplesDeliveryPutMutation,
} from "@/services/backend/samples/api/delivery-api";
import { UUID } from "@bitory-gmbh/nidavellir";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { formatDateTime } from "@/shared/lib/utilities/date";
import { Spinner } from "@/shared/components/ui/spinner";
import {
  Delivery,
  newItemFrom,
} from "@/services/backend/samples/types/delivery";
import { toast } from "sonner";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";

export function AddToDeliveryDropdown({
  selected,
  documentId,
  onSuccess,
}: {
  selected: SampleViewModel[];
  documentId?: UUID;
  onSuccess?: () => void;
}) {
  const { data, isLoading, error } = useSamplesDeliveryListQuery({
    document: {
      active: documentId !== undefined,
      values: [documentId ?? NullUUID],
    },
  });

  const [put, { isLoading: putIsLoading, reset }] =
    useSamplesDeliveryPutMutation();

  const addToDelivery = (delivery: Delivery) => {
    if (putIsLoading) return;

    put({
      ...delivery,
      items: [
        ...delivery.items,
        ...selected
          // filter out duplicates
          .filter((s) => !delivery.items.some((di) => di.sampleId === s.id))
          .map((s) => newItemFrom(s, delivery.id)),
      ],
    })
      .unwrap()
      .then(() => {
        onSuccess?.();
        toast.success(t("Proben hinzugefügt"));
        reset();
      })
      .catch((e) => {
        toast(...parseRTKQueryErrorToSonner(e));
        reset();
      });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button size="sm" variant="secondary">
          <span>{t("Zu Lieferung hinzufügen")}</span>
          <ChevronDown />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuGroup>
          <DropdownMenuLabel>
            {t(`${selected.length} Proben ausgewählt`)}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t(`Lieferungen:`)}</DropdownMenuLabel>
          {error && <RTKQueryErrorAlert error={error} className="border-0" />}
          {isLoading && (
            <span className="flex items-center gap-2 px-2">
              <Spinner />
              {t("Lade ...")}
            </span>
          )}
          {data &&
            data.items.map((delivery) => (
              <DropdownMenuItem
                key={delivery.id}
                onClick={() => addToDelivery(delivery)}
              >{`${formatDateTime(delivery.createdAt)} TrackingID: ${delivery.trackingId}`}</DropdownMenuItem>
            ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
